// @ts-ignore: moment declaration file
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'
import type { TFunction } from 'i18next'

import {
    getReservationDuration,
    getDisplayPriceFromCents,
} from '@/components/checkout/order-summary/utils'
import { getReservationBasePath } from '../../helpers'
import {
    WEB_RESERVATION_ROUTE_SEGMENTS,
    WEB_PROFILE_ROUTE_SEGMENTS,
} from '../../constants'
import type { SearchSubmissionType } from '../../types'
import type { CheckoutSuccessPageDataPayload } from '../../types/reservation-checkout'
import { ROUTE_SEGMENTS } from '../../pwa/constants'

type SupportedLocale = keyof typeof WEB_PROFILE_ROUTE_SEGMENTS.profile

export interface CheckoutSuccessSummaryViewModel {
    garageLine: string
    ownerName: string
    ownerMeta: string
    beginsAtDate: string
    beginsAtTime: string
    endsAtDate: string
    endsAtTime: string
    reservationDays: number
    reservationHours: number
    amount: number
    withVat: string
    name: string
    email: string
    phoneNumber: string
    phoneNumber2: string
    carColor: string
    numberPlate: string
    floorWithNumber: string
}

export interface CheckoutSuccessRepeatWebLinks {
    sameTermHref: string
    nextDayHref: string
}

export interface CheckoutSuccessRepeatSearchSubmissions {
    sameTerm: SearchSubmissionType
    nextDay: SearchSubmissionType
}

export function isRichCheckoutPaymentStatus(status: string): boolean {
    return status === 'successful' || status === 'created'
}

export function mapCheckoutSuccessPageDataToSummary(
    data: CheckoutSuccessPageDataPayload,
    t: TFunction,
): CheckoutSuccessSummaryViewModel {
    const garageLine = [data.garage?.name, data.garage?.locality]
        .filter(Boolean)
        .join(', ')
    const ownerMeta = [data.owner?.street, data.owner?.postal_code, data.owner?.ic]
        .filter(Boolean)
        .join(', ')
    const { days, hours } = getReservationDuration(
        data.reservation.begins_at,
        data.reservation.ends_at,
    )
    return {
        garageLine,
        ownerName: data.owner?.name ?? '',
        ownerMeta,
        beginsAtDate: moment(data.reservation.begins_at).format('DD.MM.YYYY'),
        beginsAtTime: moment(data.reservation.begins_at).format('HH:mm').replace('00:00', '00:01'),
        endsAtDate: moment(data.reservation.ends_at).format('DD.MM.YYYY'),
        endsAtTime: moment(data.reservation.ends_at).format('HH:mm').replace('00:00', '00:01'),
        reservationDays: days,
        reservationHours: hours,
        amount: getDisplayPriceFromCents(data.reservation.amount_cents),
        withVat: data.owner?.vat_payer ? t('success.reservationSummary.vatIncluded') : '',
        name: data.reservation.name ?? '',
        email: data.reservation.email ?? '',
        phoneNumber: data.reservation.phone_number ?? '',
        phoneNumber2: data.reservation.phone_number_2 ?? '',
        carColor: data.reservation.car_color ?? '',
        numberPlate: data.reservation.number_plate ?? '',
        floorWithNumber: data.reservation.floor_with_number ?? '',
    }
}

function getNormalizedRepeatTime(value: string): string {
    return moment(value).format('HH:mm').replace('00:00', '00:01')
}

function getRepeatReservationParams(
    data: CheckoutSuccessPageDataPayload,
    dayOffset: number,
): URLSearchParams {
    const beginsAt = moment(data.reservation.begins_at).add(dayOffset, 'days')
    const endsAt = moment(data.reservation.ends_at).add(dayOffset, 'days')

    return new URLSearchParams({
        'reservation_step_reservation_time[begins_at_date]': beginsAt.format('YYYY-MM-DD'),
        'reservation_step_reservation_time[begins_at_time]': getNormalizedRepeatTime(data.reservation.begins_at),
        'reservation_step_reservation_time[ends_at_date]': endsAt.format('YYYY-MM-DD'),
        'reservation_step_reservation_time[ends_at_time]': getNormalizedRepeatTime(data.reservation.ends_at),
        'reservation_step_reservation_time[garage_id]': String(data.garage?.id ?? ''),
    })
}

export function getCheckoutSuccessRepeatWebLinks(
    data: CheckoutSuccessPageDataPayload,
    locale: string,
): CheckoutSuccessRepeatWebLinks | null {
    if (!data.garage) {
        return null
    }

    const localeKey = locale as SupportedLocale
    const reservationBasePath = getReservationBasePath(
        locale,
        WEB_RESERVATION_ROUTE_SEGMENTS.reservations[localeKey],
        WEB_RESERVATION_ROUTE_SEGMENTS.reservationTime[localeKey],
        data.garage.reservation_slug,
    )

    return {
        sameTermHref: `${reservationBasePath}?${getRepeatReservationParams(data, 0).toString()}`,
        nextDayHref: `${reservationBasePath}?${getRepeatReservationParams(data, 1).toString()}`,
    }
}

export function getCheckoutSuccessRepeatSearchSubmissions(
    data: CheckoutSuccessPageDataPayload,
): CheckoutSuccessRepeatSearchSubmissions {
    return {
        sameTerm: {
            startDate: moment(data.reservation.begins_at),
            startTime: getNormalizedRepeatTime(data.reservation.begins_at),
            endDate: moment(data.reservation.ends_at),
            endTime: getNormalizedRepeatTime(data.reservation.ends_at),
            location: null,
        },
        nextDay: {
            startDate: moment(data.reservation.begins_at).add(1, 'days'),
            startTime: getNormalizedRepeatTime(data.reservation.begins_at),
            endDate: moment(data.reservation.ends_at).add(1, 'days'),
            endTime: getNormalizedRepeatTime(data.reservation.ends_at),
            location: null,
        },
    }
}

export function getCheckoutSuccessAction({
    isPwa,
    isLoggedIn,
    locale,
    isFallback,
    checkoutPath,
    t,
}: {
    isPwa: boolean
    isLoggedIn: boolean
    locale: string
    isFallback: boolean
    checkoutPath: string
    t: TFunction
}) {
    if (isFallback) {
        return {
            actionHref: checkoutPath,
            actionLabel: t('checkout.paymentResult.backToCheckout'),
        }
    }

    if (isPwa) {
        return {
            actionHref: `/pwa/${ROUTE_SEGMENTS.myReservations}`,
            actionLabel: t('success.congratulations.myReservationsLink'),
        }
    }

    if (isLoggedIn) {
        const localeKey = locale as keyof typeof WEB_PROFILE_ROUTE_SEGMENTS.profile

        return {
            actionHref: `/${locale}/${WEB_PROFILE_ROUTE_SEGMENTS.profile[localeKey]}/${WEB_PROFILE_ROUTE_SEGMENTS.reservations[localeKey]}`,
            actionLabel: t('success.congratulations.myReservationsLink'),
        }
    }

    return {
        actionHref: `/${locale}`,
        actionLabel: t('checkout.paymentResult.backToHomepage'),
    }
}
